'use client';
import { useSignUpFlowStore } from '@/app/store/signUpFlow.store';
import SocialAuth from '@/components/auth/social-auth';
import { AuthSuccess } from '@/components/card/auth-succes';
import ErrorCard from '@/components/card/error-card';
import { PasswordInputCustom } from '@/components/input/password-input';
import { AuthLayout } from '@/components/layout/auth/auth-layout';
import SmallerScreensModal from '@/components/modal/smaller-screens-modal';
import { CompanyLogo } from '@/components/other/company-logo';
import { PasswordCheck } from '@/components/other/password-check';
import { useAuth } from '@/hooks/mutate/use-auth';
import { useInitializePayment } from '@/hooks/query/subscriptions';
import { routes } from '@/lib/routes';
import { otpSchema, signUpSchema } from '@/lib/validator/auth';
import CubeIcon from '@/public/assets/svg/cube-icon';
import DotBurstIcon from '@/public/assets/svg/dot-burst-icon';
import { IconArrowLeft } from '@/public/assets/svg/icon-arrow-left';
import LoadingDotsIcon from '@/public/assets/svg/loading-dots-icon';
import { FormSignUpValue } from '@/types/auth';
import {
  Button,
  Card,
  Center,
  Flex,
  Loader,
  PinInput,
  SimpleGrid,
  Text,
  TextInput,
  Transition,
  UnstyledButton,
  useMantineTheme,
} from '@mantine/core';
import { useForm, yupResolver } from '@mantine/form';
import { useMediaQuery } from '@mantine/hooks';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useMemo, useState } from 'react';

interface FormSignUpValueWithReferral extends FormSignUpValue {
  ref?: string;
}

const initialValues: FormSignUpValueWithReferral = {
  firstName: '',
  lastName: '',
  password: '',
  email: '',
  ref: '',
};

const SignUpPage = () => {
  const router = useRouter();
  const theme = useMantineTheme();
  const pathname = usePathname();
  const isCRESignup = pathname.includes('/cre');
  const searchParams = useSearchParams();
  const refParam = searchParams.get('ref');
  const planParam = searchParams.get('plan');

  const { tab, setTab } = useSignUpFlowStore();
  const [error, setError] = useState('');
  const [timeLeft, setTimeLeft] = useState<number>(0); // Countdown time in seconds
  const [isDisabled, setIsDisabled] = useState<boolean>(false);

  const {
    onVerifyEmail,
    onSignUp,
    isLoading,
    isSending,
    onSendEmail,
    onSocialAuth,
    isLoadingProvider,
  } = useAuth();
  const {
    initializePayment,
    isPending,
    data: paymentData,
  } = useInitializePayment();

  const form = useForm<FormSignUpValueWithReferral>({
    // Updated form type
    initialValues,
    validate: yupResolver(signUpSchema),
  });
  const socialReferral =
    isCRESignup && (refParam || form.values.ref)
      ? refParam || form.values.ref
      : undefined;
  const otpForm = useForm({
    initialValues: {
      otp: '',
    },
    validate: yupResolver(otpSchema),
  });

  useEffect(() => {
    if (!(isCRESignup && (refParam === 'chapman'))) {
      router.replace(routes.auth.login);
    }
  }, [isCRESignup, refParam, router]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft((prevTime) => prevTime - 1);
      }, 1000);
    } else {
      setIsDisabled(false); // Enable the button when countdown ends
    }
    return () => clearInterval(timer);
  }, [timeLeft]);
  useEffect(() => {
    if (paymentData) {
      // Redirect to the payment URL
      window.location.href = paymentData?.client_use;
    }
  }, [paymentData]);

  const handleResendOTP = useCallback(() => {
    onSendEmail({
      value: {
        email: form.values.email,
        firstName: form.values.firstName,
      },
      cb: () => {
        setTimeLeft(30);
        setIsDisabled(true);
      },
    });
  }, [onSendEmail, form.values.email, form.values.firstName]);

  const refList = useMemo(
    () => ({
      title: 'Welcome to Exofinance',
      list: [
        {
          title: (
            <div>
              <Text component="span" className="font-medium text-p1">
                Immediate Data Parsing:{' '}
              </Text>
              <span>
                Extracts data from financial models, rent roll data, and leases
                into structured datasets{' '}
              </span>
            </div>
          ),
          icon: <CubeIcon />,
        },
        {
          title: (
            <div>
              <Text component="span" className="font-medium text-p1">
                From Complexity to Clarity:{' '}
              </Text>
              Model cashflows, create waterfall charts, automate diligence
              reports
            </div>
          ),
          icon: <DotBurstIcon />,
        },
        {
          title: (
            <div>
              <Text component="span" className="font-medium text-p1">
                Interactive AI Insights:{' '}
              </Text>
              Access a KPI dashboard showcasing{' '}
              <Text component="span" className="font-medium">
                Ask anything, test scenarios, get answers: think ChatGPT for CRE
              </Text>
            </div>
          ),
          icon: <LoadingDotsIcon />,
        },
      ],
    }),
    []
  );

  const handleContinueToPayment = useCallback(() => {
    if (planParam) {
      initializePayment({ plan_id: planParam });
    }
  }, [initializePayment, planParam]);

  const handleSignUpSubmit = useCallback(() => {
    const payload: {
      email: string;
      password: string;
      agent?: string;
      referal?: string;
    } = {
      email: form.values.email,
      password: form.values.password,
      ...(isCRESignup && { agent: 'cre' }),
    };

    if (isCRESignup) {
      if (refParam) {
        payload.referal = refParam;
      } else if (form.values.ref) {
        payload.referal = form.values.ref;
      }
    }

    onVerifyEmail({
      values: payload,
      errorCallBack: (e) => setError(e),
      successCallBack: () => {
        onSendEmail({
          value: {
            email: form.values.email,
            firstName: form.values.firstName,
          },
          cb: () => {
            setTimeLeft(30);
            setIsDisabled(true);
            setTab('otp');
            setError('');
          },
        });
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    onVerifyEmail,
    onSendEmail,
    form.values.email,
    form.values.firstName,
    setTab,
    setTimeLeft,
    setIsDisabled,
    setError,
    isCRESignup,
    refParam,
    form.values.ref,
  ]);

  const handleOTPSubmit = useCallback(() => {
    onSignUp({
      values: {
        ...form.values,
        otp: parseInt(otpForm.values.otp, 10),
        ...(isCRESignup && { agent: 'cre' }),
        ...(form.values.ref && { referal: form.values.ref }),
        ...(refParam && { referal: refParam }),
      },
      errorCallBack: (e) => setError(e),
      successCallBack: () => {
        setTab('success');
        setError('');
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onSignUp, form.values, otpForm.values.otp, setTab, setError]);

  const isNotLargeScreen = useMediaQuery('(min-width: 350px)');
  if (!isNotLargeScreen) {
    return <SmallerScreensModal />;
  }
  return (
    <AuthLayout
      sidelist={refList}
      showLeftOnMobile={true}
      hideLeft={tab !== 'signup' ? true : false}
    >
      <Flex className="flex-col items-center justify-center py-10">
        {tab !== 'signup' && (
          <Center mb={'md'}>
            <CompanyLogo />
          </Center>
        )}
        <Transition
          enterDelay={500}
          exitDelay={300}
          mounted={tab === 'signup'}
          transition="fade"
          duration={400}
          timingFunction="ease"
        >
          {(styles) => (
            <form
              className="w-full"
              onSubmit={form.onSubmit(handleSignUpSubmit)}
            >
              <Card
                className="auth-card"
                style={{
                  ...styles,
                  display: tab === 'signup' ? 'block' : 'none',
                }}
              >
                <Text className="auth-text-header">Create an account</Text>
                <div className="auth-text-title">
                  Already have an account? {'      '}
                  <UnstyledButton
                    onClick={() => router.push(routes.auth.login)}
                    mr={'md'}
                  >
                    <Text
                      className="auth-text-title !text-vibrantgreen"
                      c={theme.colors[theme.primaryColor][6]}
                      fw={700}
                    >
                      Sign in
                    </Text>
                  </UnstyledButton>
                </div>
                <SocialAuth
                  onSocialAuth={onSocialAuth}
                  isLoadingProvider={isLoadingProvider}
                  agent={isCRESignup ? 'cre' : ''}
                  ref={socialReferral}
                  title="Sign up with "
                />

                <ErrorCard error={error} />
                <SimpleGrid mb={'md'} cols={{ base: 1, sm: 2 }}>
                  <TextInput
                    {...form.getInputProps('firstName')}
                    placeholder="John"
                    label="First name"
                  />
                  <TextInput
                    {...form.getInputProps('lastName')}
                    placeholder="Doe"
                    label="Last name"
                  />
                </SimpleGrid>
                <TextInput
                  {...form.getInputProps('email')}
                  placeholder="Acme corporation"
                  label="Work email"
                  mb={'md'}
                />
                <PasswordInputCustom
                  mb={'1rem'}
                  placeholder={'hi@acmecorporation.com'}
                  {...form.getInputProps('password')}
                />
                <PasswordCheck
                  password={form.values.password}
                  email={form.values.email}
                  firstName={form.values.firstName}
                  lastName={form.values.lastName}
                />
                {/* {isCRESignup && (
                  <TextInput
                    {...form.getInputProps('ref')}
                    placeholder="Optional"
                    label="Referral Code"
                    mb={'md'}
                  />
                )} */}
                <Button
                  size="md"
                  variant="primary"
                  loading={isLoading}
                  type="submit"
                  w={'100%'}
                  mb={'2rem'}
                  mt={'1rem'}
                >
                  Sign Up
                </Button>
                <div className="auth-text-title">
                  By signing up, you acknowledge that you have read, understood,
                  and agree to Exo AI’s{' '}
                  <UnstyledButton component="span">
                    <Text
                      target="_blank"
                      href="https://exoai.tech/term-services"
                      component="a"
                      className="auth-text-title mr-1 !text-vibrantgreen hover:underline"
                      c={theme.colors[theme.primaryColor][6]}
                      fw={700}
                    >
                      Terms of Service.
                    </Text>
                  </UnstyledButton>
                  We respect your privacy and will never sell your data.
                  <UnstyledButton component="span">
                    <Text
                      target="_blank"
                      href="https://exoai.tech/term-services"
                      component="a"
                      className="auth-text-title ml-1 !text-vibrantgreen hover:underline"
                      c={theme.colors[theme.primaryColor][6]}
                      fw={700}
                    >
                      Privacy Policy
                    </Text>
                  </UnstyledButton>
                </div>
                <p className="text-[14px] italic text-gray-500">
                  NOTE: For the best experience, we recommend using this
                  platform on desktop or laptop computers.
                </p>
              </Card>
            </form>
          )}
        </Transition>
        <Transition
          mounted={tab === 'otp'}
          transition="slide-left"
          duration={400}
          timingFunction="ease"
        >
          {(styles) => (
            <form
              className="flex w-full justify-center"
              onSubmit={otpForm.onSubmit(handleOTPSubmit)}
            >
              <Card
                className="auth-small-card"
                style={{
                  ...styles,
                  display: tab === 'otp' ? 'block' : 'none',
                }}
              >
                <Button
                  size="md"
                  className="mb-4 bg-transparent px-0"
                  variant="white"
                  onClick={() => {
                    setError('');
                    setTab('signup');
                  }}
                >
                  <Flex className="gap-2">
                    <IconArrowLeft />
                    <Text
                      c={theme.colors[theme.primaryColor][6]}
                      className="hidden text-[14px] font-normal md:block"
                    >
                      Back to Signup
                    </Text>
                  </Flex>
                </Button>
                <Text className="auth-text-header">Verify your email</Text>
                <Text mb={'1.5rem'} className="auth-text-title">
                  Enter the 6 digit code sent to your email address
                </Text>
                <ErrorCard error={error} />
                <Center mb={'2rem'}>
                  <PinInput
                    type="number"
                    classNames={{
                      root: 'justify-between w-full',
                      input: '!font-bold !text-[1.1rem]',
                    }}
                    {...otpForm.getInputProps('otp')}
                    placeholder=""
                    length={6}
                    size="lg"
                  />
                </Center>
                <Button
                  size="md"
                  loading={isLoading}
                  className="mb-5"
                  w={'100%'}
                  type="submit"
                >
                  Verify your email address
                </Button>
                <div className="auth-text-title flex items-center justify-center text-center">
                  Didn’t receive any code?{' '}
                  {isDisabled ? (
                    `You can resend the OTP in ${timeLeft} seconds`
                  ) : (
                    <>
                      {isSending ? (
                        <Loader className="ml-2" size={'xs'} />
                      ) : (
                        <>
                          <UnstyledButton
                            className="mr-2"
                            onClick={handleResendOTP}
                            disabled={isDisabled}
                          >
                            <Text
                              component="span"
                              className="auth-text-title pl-2"
                              c={theme.colors[theme.primaryColor][6]}
                              fw={700}
                            >
                              Resend code
                            </Text>
                          </UnstyledButton>
                        </>
                      )}
                    </>
                  )}
                </div>
              </Card>
            </form>
          )}
        </Transition>
        <AuthSuccess
          description="
          Thank you for your
          interest in our cutting-edge technology."
          title="Account created successfully"
          isVisible={tab === 'success'}
        >
          <Button
            size="md"
            onClick={() =>
              planParam
                ? handleContinueToPayment()
                : router.push(routes.auth.login)
            }
          >
            {!planParam
              ? 'Continue to app'
              : isPending
                ? '...'
                : 'Continue to Payment'}
          </Button>
        </AuthSuccess>
      </Flex>
    </AuthLayout>
  );
};
export default SignUpPage;
